/**
 * Watrly Modal Component
 * ======================
 * مكون النافذة المنبثقة
 */

import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './Modal.css';

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md', // sm | md | lg | xl | full
  closeOnOverlay = true,
  closeOnEsc = true,
  showCloseButton = true,
  className = '',
  ...props
}) => {
  const { isRTL, t } = useTheme();
  const modalRef = useRef(null);

  // إغلاق بالضغط على Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (closeOnEsc && e.key === 'Escape' && isOpen) {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeOnEsc, isOpen, onClose]);

  // منع التمرير عند فتح المودال
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // التركيز على المودال عند الفتح
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlay && e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const classes = [
    'watrly-modal__content',
    `watrly-modal__content--${size}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className="watrly-modal"
      onClick={handleOverlayClick}
      dir={isRTL ? 'rtl' : 'ltr'}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      {...props}
    >
      <div
        ref={modalRef}
        className={classes}
        tabIndex={-1}
      >
        {(title || showCloseButton) && (
          <div className="watrly-modal__header">
            {title && (
              <h2 id="modal-title" className="watrly-modal__title">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                className="watrly-modal__close"
                onClick={onClose}
                aria-label={t?.('general.close') || 'إغلاق'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className="watrly-modal__body">{children}</div>

        {footer && <div className="watrly-modal__footer">{footer}</div>}
      </div>
    </div>
  );
};

// مودال التأكيد
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'danger', // danger | warning | info
  loading = false,
}) => {
  const { t } = useTheme();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title || t?.('messages.confirmAction') || 'تأكيد'}
      size="sm"
      footer={
        <div className="watrly-modal__confirm-actions">
          <button
            className="watrly-modal__cancel-btn"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText || t?.('general.cancel') || 'إلغاء'}
          </button>
          <button
            className={`watrly-modal__confirm-btn watrly-modal__confirm-btn--${variant}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <svg className="watrly-modal__spinner" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
              </svg>
            ) : (
              confirmText || t?.('general.confirm') || 'تأكيد'
            )}
          </button>
        </div>
      }
    >
      <p className="watrly-modal__confirm-message">{message}</p>
    </Modal>
  );
};

export default Modal;
